import React, { useState } from 'react';
import {
  AppBar, Toolbar, Box, IconButton, Avatar, alpha, Menu, MenuItem,
  ListItemIcon, Typography, Divider, Tooltip,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon, LogOut, User, Settings as SettingsIcon, Moon, Sun, HelpCircle, RefreshCw,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../redux/slices/authSlice';
import { useColorMode } from '../theme/ColorModeContext';
import NotificationBell from './NotificationBell';
import NotificationCenter from './NotificationCenter';
import { DRAWER_WIDTH } from './Sidebar';

// Paths whose URL doesn't read as the page name ("/messages" is the
// Conversations page, "/wbr" would title-case to "Wbr").
const PAGE_TITLES = { '/messages': 'Conversations', '/wbr': 'WBR Report', '/rfq': 'RFQs', '/sop': 'SOPs' };

const labelize = (path) => {
  if (!path || path === '/') return 'Dashboard';
  if (PAGE_TITLES[path]) return PAGE_TITLES[path];
  return path
    .replace(/^\//, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

const Navbar = ({ onToggle }) => {
  const theme = useTheme();
  const { mode, toggleColorMode } = useColorMode();
  const isDark = mode === 'dark';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((s) => s.auth) || {};

  const [profileAnchor, setProfileAnchor] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Reload the current page. A hard reload is intentional: it re-fetches data on
  // every page (including ones that cache in Redux) and keeps the admin logged
  // in (the auth token is restored from localStorage on init). With the service
  // worker caching the app shell, the reload is fast.
  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => window.location.reload(), 150); // let the spin show first
  };

  const displayName =
    user?.name ||
    `${user?.first_name || ''} ${user?.last_name || ''}`.trim() ||
    'Administrator';
  const initials =
    displayName
      .split(' ')
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'A';

  const handleLogout = () => {
    dispatch(logout());
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.replace('/signin');
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: alpha(theme.palette.background.paper, 0.86),
          backdropFilter: 'saturate(180%) blur(12px)',
          color: 'text.primary',
          boxShadow: 'none',
          zIndex: (t) => t.zIndex.drawer + 0.5,
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          right: 0,
          left: 'auto',
          // Grow under the status bar / notch on installed PWAs, then pad the
          // bar back down by the same inset so its content never hides.
          height: 'calc(68px + env(safe-area-inset-top, 0px))',
          pt: 'env(safe-area-inset-top, 0px)',
          justifyContent: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            pl: { xs: 'max(12px, env(safe-area-inset-left, 0px))', md: 3, lg: 4 },
            pr: { xs: 'max(12px, env(safe-area-inset-right, 0px))', md: 3, lg: 4 },
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 }, minWidth: 0, flex: 1 }}>
            <IconButton
              onClick={onToggle}
              sx={{ display: { md: 'none' }, color: 'text.secondary' }}
              aria-label="Open navigation"
            >
              <MenuIcon size={22} />
            </IconButton>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1rem', md: '1.05rem' },
                  color: 'text.primary',
                  lineHeight: 1.2,
                }}
                noWrap
              >
                {labelize(location.pathname)}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                Reseller Bazzar admin
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5 } }}>
            <Tooltip title="Refresh page">
              <IconButton
                onClick={handleRefresh}
                aria-label="Refresh page"
                sx={{
                  color: 'text.secondary',
                  '& svg': { animation: refreshing ? 'rbSpin 0.6s linear infinite' : 'none' },
                  '@keyframes rbSpin': { to: { transform: 'rotate(360deg)' } },
                }}
              >
                <RefreshCw size={20} />
              </IconButton>
            </Tooltip>

            <NotificationCenter />

            <NotificationBell />

            <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton
                onClick={toggleColorMode}
                sx={{ color: 'text.secondary' }}
                aria-label="Toggle dark mode"
              >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Help & support">
              <IconButton
                component="a"
                href="mailto:support@resellerbazzar.in?subject=Admin%20Support%20Request"
                sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'inline-flex' } }}
                aria-label="Help"
              >
                <HelpCircle size={20} />
              </IconButton>
            </Tooltip>

            <IconButton
              onClick={(e) => setProfileAnchor(e.currentTarget)}
              sx={{ ml: 0.5, p: 0.4 }}
              aria-label="Account menu"
            >
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: 'primary.main',
                  fontWeight: 800,
                  fontSize: 14,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                }}
              >
                {initials}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 3,
              minWidth: 240,
              boxShadow: '0 16px 32px rgba(20,26,56,0.12)',
              border: `1px solid ${theme.palette.divider}`,
              p: 0.5,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{displayName}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
            {user?.email || 'admin@resellerbazzar.in'}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => { setProfileAnchor(null); navigate('/settings'); }}>
          <ListItemIcon><User size={16} /></ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => { setProfileAnchor(null); navigate('/settings'); }}>
          <ListItemIcon><SettingsIcon size={16} /></ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={() => { toggleColorMode(); }}>
          <ListItemIcon>{isDark ? <Sun size={16} /> : <Moon size={16} />}</ListItemIcon>
          {isDark ? 'Light mode' : 'Dark mode'}
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={handleLogout}
          sx={{ color: 'error.main', fontWeight: 700 }}
        >
          <ListItemIcon><LogOut size={16} color={theme.palette.error.main} /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default Navbar;
